import { useState, useEffect, useRef } from "react";

function useInView() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.15 });
    const el = ref.current;
    if (el) obs.observe(el);
    return () => { if (el) obs.unobserve(el); };
  }, []);
  return [ref, visible];
}

function Section({ children, title, subtitle, id }) {
  const [ref, visible] = useInView();
  return (
    <section
      ref={ref}
      id={id}
      style={{
        padding: '80px 24px',
        maxWidth: '1100px',
        margin: '0 auto',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(30px)',
        transition: 'opacity 0.8s ease, transform 0.8s ease',
      }}
    >
      {title && (
        <div style={{ textAlign: 'center', marginBottom: '60px' }}>
          <h2 style={{ fontSize: 'clamp(22px, 4vw, 42px)', fontWeight: 900, color: '#111', textTransform: 'uppercase', letterSpacing: '-1px' }}>{title}</h2>
          {subtitle && <p style={{ fontSize: '14px', fontWeight: 700, color: '#006600', marginTop: '10px', textTransform: 'uppercase', letterSpacing: '2px' }}>{subtitle}</p>}
          <div style={{ height: '4px', width: '60px', background: '#006600', margin: '20px auto 0', borderRadius: '2px' }} />
        </div>
      )}
      {children}
    </section>
  );
}

export default function Bussiness() {
  return (
    <div style={{ background: '#ffffff', color: '#1a1a1a', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <style>{`
        .biz-card {
          background: #fbfbfb;
          border: 1px solid #eee;
          border-radius: 24px;
          padding: 32px;
          height: 100%;
          transition: all 0.3s ease;
          box-sizing: border-box;
        }
        .biz-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 20px 40px rgba(0,0,0,0.05);
          border-color: #006600;
        }
        .biz-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 24px;
        }
        .benefit-item {
          display: flex;
          gap: 16px;
          margin-bottom: 24px;
          align-items: flex-start;
        }
        .benefit-icon {
          width: 48px; height: 48px; border-radius: 12px; background: rgba(0,102,0,0.08);
          display: grid; place-items: center; color: #006600; font-size: 20px; flex-shrink: 0;
        }
        .benefit-content h4 { margin: 0 0 6px 0; font-size: 18px; font-weight: 800; }
        .benefit-content p { margin: 0; font-size: 14px; color: #666; line-height: 1.6; }

        .income-pill {
          display: inline-flex; align-items: center; gap: 8px; padding: 6px 16px;
          background: #fff; border: 1px solid #eee; border-radius: 99px;
          font-size: 13px; font-weight: 700; margin: 4px;
        }
        .income-pill b { color: #006600; }

        .awards-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
          gap: 16px;
          margin-top: 30px;
        }
        .award-mini {
          padding: 14px; background: #fff; border: 1px solid #eee; border-radius: 16px;
          display: flex; align-items: center; gap: 10px; font-size: 13px; font-weight: 700;
        }

        /* Opportunity responsive grid */
        .opp-grid {
          display: grid;
          grid-template-columns: 1fr 320px;
          gap: 60px;
          align-items: center;
        }
        .opp-img {
          width: 100%;
          height: 320px;
          background: #eee;
          border-radius: 24px;
          overflow: hidden;
          box-shadow: 0 20px 40px rgba(0,0,0,0.1);
        }
        .opp-img img { width: 100%; height: 100%; object-fit: cover; }

        @media(max-width: 768px) {
          .opp-grid {
            grid-template-columns: 1fr;
            gap: 28px;
          }
          .opp-img { height: 220px; }
          .biz-card { padding: 20px; }
          .benefit-content h4 { font-size: 16px; }
        }
      `}</style>

      {/* Hero Section */}
      <div style={{ background: '#006600', padding: 'clamp(60px, 12vw, 120px) 24px clamp(50px, 10vw, 100px)', textAlign: 'center', color: '#fff' }}>
        <h1 style={{ fontSize: 'clamp(32px, 8vw, 72px)', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '-2px', marginBottom: '16px' }}>SUN DISTRIBUTORS</h1>
        <p style={{ fontSize: 'clamp(15px, 2.5vw, 22px)', fontWeight: 500, opacity: 0.9, letterSpacing: '1px' }}>Change Your Thought, Change Your Life</p>
      </div>

      {/* WHY SUN DISTRIBUTORS */}
      <Section id="why-sun-distributors" title="Why SUN DISTRIBUTORS is the Right Choice?" subtitle="Foundations of Success">
        <div className="biz-grid">
          <div className="biz-card">
            <p style={{ fontSize: '17px', lineHeight: 1.8, color: '#444', textAlign: 'justify' }}>
              <strong>SUN DISTRIBUTORS</strong> offers a unique opportunity for everyone to start a business with low investment and high earning potential.
              Our mission is to create distributors across India through our <strong>Distributors Manufacturing Model</strong>, helping people build sustainable income and self-confidence.
            </p>
            <p style={{ marginTop: '20px', color: '#666', lineHeight: 1.8 }}>
              SUN DISTRIBUTORS gives you the chance to become a franchise partner — no experience required, no risk, and full support.
              With our wide range of 9 product categories and professional business training, you can start earning from day one.
            </p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {[
              { t: "Low Investment", d: "Start your business with minimal cost" },
              { t: "No Risk", d: "Full support, proven model" },
              { t: "High Reward", d: "Earn income through your own franchise" },
              { t: "Wide Range", d: "9 categories of daily-use products" },
              { t: "Full Training", d: "Business, marketing, and product knowledge" }
            ].map((adv, i) => (
              <div key={i} style={{ padding: '16px 20px', background: '#f9f9f9', borderRadius: '16px', borderLeft: '4px solid #006600' }}>
                <h4 style={{ margin: '0 0 4px 0', fontSize: '15px', fontWeight: 800 }}>{adv.t}</h4>
                <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>{adv.d}</p>
              </div>
            ))}
          </div>
        </div>
        <p style={{ marginTop: '40px', textAlign: 'center', fontWeight: 600, color: '#006600' }}>
          Whether you are a youth, homemaker, RMP doctor, tailor, or retired person, SUN DISTRIBUTORS gives you the right path to financial independence.
        </p>
      </Section>

      {/* BUSINESS OPPORTUNITY */}
      <Section id="opportunity" title="Business Opportunity" subtitle="How to Start the Business">
        <div className="opp-grid">
          <div style={{ fontSize: '16px', lineHeight: 1.8, color: '#333' }}>
            <p><strong>SUN DISTRIBUTORS</strong> is a business opportunity for anyone, anywhere, anytime — whether you have free time, part time, or full time.</p>
            <p style={{ margin: '20px 0' }}>To join the SUN DISTRIBUTORS family and start your journey, you can register as a <strong>SUN DISTRIBUTORS Franchise Member — Free of Cost.</strong></p>
            <p>Every franchise partner will receive <strong>100 days of free business training</strong>, learning how to build, manage, and grow a business successfully.</p>
            <p style={{ marginTop: '20px' }}>Through SUN DISTRIBUTORS, you can earn income, gain valuable experience, and build the foundation for your future business success.</p>
          </div>
          <div className="opp-img">
            <img src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?q=80&w=2670&auto=format&fit=crop" alt="Opportunities" />
          </div>
        </div>
      </Section>

      {/* REGISTER NOW */}
      <div style={{ background: '#f5f5f7', padding: 'clamp(50px, 10vw, 100px) 24px' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div id="register" style={{ textAlign: 'center', marginBottom: '60px' }}>
            <h2 style={{ fontSize: 'clamp(20px, 4vw, 36px)', fontWeight: 900, color: '#111' }}>REGISTER AS A FRANCHISE PARTNER</h2>
            <div style={{ height: '4px', width: '60px', background: '#006600', margin: '20px auto 0' }} />
          </div>

          <div className="biz-grid">
            <div className="biz-card" style={{ background: '#fff' }}>
              <h4 style={{ fontSize: '20px', fontWeight: 800, marginBottom: '20px' }}>Requirements</h4>
              <ul style={{ padding: 0, listStyle: 'none' }}>
                <li style={{ marginBottom: '12px', display: 'flex', gap: '10px' }}>✅ <span>18 years or older</span></li>
                <li style={{ marginBottom: '12px', display: 'flex', gap: '10px' }}>✅ <span>Basic knowledge of business interest</span></li>
                <li style={{ marginBottom: '12px', display: 'flex', gap: '10px' }}>✅ <span>Commitment to 100 days training</span></li>
              </ul>
            </div>
            <div className="biz-card" style={{ background: '#fff' }}>
              <h4 style={{ fontSize: '20px', fontWeight: 800, marginBottom: '20px' }}>How to Register?</h4>
              <p style={{ fontSize: '14px', color: '#444' }}><strong>Option 1:</strong> Meet your nearest <strong>Senior SUN DISTRIBUTORS Distributor</strong> — they will guide you and help complete your franchise joining process.</p>
              <hr style={{ margin: '20px 0', border: 0, borderTop: '1px solid #eee' }} />
              <p style={{ fontSize: '14px', color: '#444' }}><strong>Option 2:</strong> Visit your nearest <strong>SUN DISTRIBUTORS Branch or Franchise Center</strong> for more information and support.</p>
            </div>
          </div>
        </div>
      </div>

      {/* BENEFITS */}
      <Section id="benefits" title="Benefits &amp; Rewards" subtitle="Of Being a SUN DISTRIBUTORS Franchise Partner">
        <div style={{ marginBottom: '40px', background: '#fcfcfc', border: '1px solid #eee', padding: '28px', borderRadius: '20px' }}>
          <p style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#444' }}>Now that you have joined the SUN DISTRIBUTORS Family, you can enjoy a wide range of exciting benefits and lifelong rewards:</p>
        </div>

        <div className="biz-grid">
          <div className="benefit-item">
            <div className="benefit-icon">🏢</div>
            <div className="benefit-content">
              <h4>Zero Investment</h4>
              <p>SUN DISTRIBUTORS offers Free Franchise Registration — no joining fee or investment required. Complete freedom to start.</p>
            </div>
          </div>
          <div className="benefit-item">
            <div className="benefit-icon">🎓</div>
            <div className="benefit-content">
              <h4>Free 100 Days Training</h4>
              <p>From the very first day, you will receive Free 100 Days Business Training, guiding you step by step towards success.</p>
            </div>
          </div>
        </div>

        <div style={{ marginTop: '40px' }}>
          <h4 style={{ fontSize: '22px', fontWeight: 800, marginBottom: '24px' }}>Multiple Income Opportunities</h4>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
            <div className="income-pill">💰 <b>Retail Profit:</b> Earn up to 10% – 80% profit on every sale</div>
            <div className="income-pill">🤝 <b>Referral Income:</b> ₹5,000 bonus for every new partner</div>
            <div className="income-pill">👥 <b>Team Bonus:</b> ₹1,500 per team member added</div>
          </div>
        </div>

        <div style={{ marginTop: '60px' }}>
          <h4 style={{ fontSize: '22px', fontWeight: 800, marginBottom: '24px' }}>Awards &amp; Recognition</h4>
          <p style={{ fontStyle: 'italic', color: '#666' }}>As your business grows, SUN DISTRIBUTORS rewards your achievements with:</p>
          <div className="awards-grid">
            {[
              { i: "📺", t: "32-inch LED TV Awards" },
              { i: "💎", t: "Gold & Silver Awards" },
              { i: "🚗", t: "Luxury Car (₹50 Lakhs)" },
              { i: "🏠", t: "Dream House (₹50 Lakhs)" },
              { i: "✈️", t: "International Tour Awards" },
              { i: "📈", t: "Personal Growth Support" }
            ].map((a, i) => (
              <div key={i} className="award-mini">
                <span style={{ fontSize: '20px' }}>{a.i}</span>
                <span>{a.t}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ marginTop: '80px', padding: 'clamp(24px, 5vw, 40px)', background: '#006600', color: '#fff', borderRadius: '24px', textAlign: 'center' }}>
          <h3 style={{ fontSize: 'clamp(18px, 3vw, 24px)', fontWeight: 800, marginBottom: '12px' }}>Change Your Thought, Change Your Life With <strong>SUN DISTRIBUTORS</strong></h3>
          <p style={{ opacity: 0.9, fontSize: '16px', maxWidth: '600px', margin: '0 auto' }}>With SUN DISTRIBUTORS, your success is unlimited and your dreams are achievable. Join the movement that's changing lives across India.</p>
        </div>
      </Section>
    </div>
  );
}